/**
 * BattleScene (SceneKeys.BATTLE, owner: combat-builder) — the single tutorial bout 师徒切磋.
 *
 * Launched in parallel over the caller by `runBattle()` (which pauses the caller); presents the
 * pure `BattleEngine` step by step: speed-ordered turn queue with 行动预告, command menu
 * (keyboard ↑↓ / Enter, pointer, touch), HP/SP bars, damage numbers, hit/miss/guard feedback,
 * Chinese battle log, scripted tutor lines through the shared dialogue box. All randomness is the
 * engine's SeededRng; presentation timing never feeds back into the outcome.
 */
import Phaser from 'phaser';
import { Depth, GAME_H, GAME_W } from '@/core/config';
import { ctxOf } from '@/core/context';
import { bus, Events } from '@/core/events';
import { SceneKeys } from '@/core/scenes/keys';
import type { BattleOutcome, IDialogueBox, IEffectsDirector, IWorldScene } from '@/core/types/contracts';
import { audio } from '@/audio';
import { battleText } from './text';
import { createEffects } from '@/effects';
import { createDialogueRunner, hooksFromContext, registerDialogues, resolveDialogueBox, type DialogueRunner } from '@/dialogue';
import { installInputMap } from '@/characters';
import { BattleEngine, type ActorState, type BattleEvent } from './engine';
import { BATTLE_ANIMS, battleAnimKey, ensureBattleBackground, ensureBattleSprite } from './devSprites';
import { floatText, playFx } from './fx';
import { Banner, Bar, BattleLog, CommandMenu, HUD_DEPTH, HudCss, TurnQueue, hpColor } from './hud';
import type { BattleAnimName, BattleLaunchData, BattleSpriteSpec, SwordTrainingConfig } from './types';
import { BATTLES } from './registry';

/** Stage anchors (feet positions, 3/4 view road). Party on the left facing right, enemies on the right facing left. */
const PARTY_ANCHORS = [{ x: 200, y: 232 }, { x: 150, y: 246 }];
const ENEMY_ANCHORS = [{ x: 440, y: 228 }, { x: 496, y: 244 }];
const DEV_LABEL: Record<string, string> = { 'jiang-zichao': 'JZC', 'zhao-chengbo': 'ZCB' };

const DEFAULT_SPRITE: Omit<BattleSpriteSpec, 'texture' | 'facing' | 'dev'> = {
  frameW: 48, frameH: 64,
  anims: { idle: { frames: 4, fps: 6, loop: true }, attack: { frames: 6, fps: 12 }, skill: { frames: 8, fps: 12 }, guard: { frames: 4, fps: 8, loop: true }, hit: { frames: 4, fps: 12 } },
};
const DEV_PALETTES: BattleSpriteSpec['dev'][] = [
  { robe: '#8a6a3a', sash: '#2b3a5c', skin: '#d9a878', hair: '#2a1c14', blade: '#c9c9d1' },
  { robe: '#5a6a86', sash: '#c9b89a', skin: '#f0d6bd', hair: '#e8e2d6', beard: '#e8e2d6', blade: '#d8d8e0' },
];

interface Fighter { state: ActorState; sprite: Phaser.GameObjects.Sprite; spec: BattleSpriteSpec; home: { x: number; y: number }; dir: 1 | -1; hpBar?: Bar; nameTag?: Phaser.GameObjects.Text; intentTag?: Phaser.GameObjects.Text }

export interface BattleDebugHook {
  snapshot(): ReturnType<BattleEngine['snapshot']>;
  choose(actionId: string): boolean;
  /** True while the command menu waits for the player. */
  isMenuOpen(): boolean;
  /** True while a scripted line is on screen. */
  isTalking(): boolean;
  /** Auto-picks commands (skill when affordable, else attack) and auto-advances lines. */
  autoplay(on: boolean): void;
  isAutoplay(): boolean;
}
declare global { interface Window { __nantianBattle?: BattleDebugHook } }

export class BattleScene extends Phaser.Scene {
  private launch!: BattleLaunchData;
  private config!: SwordTrainingConfig;
  private engine!: BattleEngine;
  private fighters = new Map<string, Fighter>();
  private runner!: DialogueRunner;
  private box!: IDialogueBox;
  private effects: IEffectsDirector | null = null;
  private queue!: TurnQueue;
  private log!: BattleLog;
  private menu!: CommandMenu;
  private banner!: Banner;
  private studentHp!: Bar;
  private studentSp!: Bar;
  private studentTags!: Phaser.GameObjects.Text;
  private alive = false;
  private timeScale = 1;
  private auto = false;
  private autoTicker: Phaser.Time.TimerEvent | null = null;
  private done = false;
  private readonly waiters = new Set<() => void>();
  private readonly offs: (() => void)[] = [];

  constructor() { super(SceneKeys.BATTLE); }

  init(data: BattleLaunchData): void {
    this.launch = data;
    this.fighters = new Map();
    this.alive = true;
    this.done = false;
    this.auto = false;
    this.autoTicker = null;
    this.waiters.clear();
    this.offs.length = 0;
  }

  create(): void {
    const ctx = ctxOf(this);
    const cfg = BATTLES[this.launch.configId];
    if (!cfg) {
      console.warn(`[combat] unknown battle "${this.launch.configId}" — resolving as rounds`);
      this.finish('rounds');
      return;
    }
    this.engine = new BattleEngine(cfg, this.launch.seed);
    this.config = this.engine.config;
    this.timeScale = ctx.url.fast || ctx.settings.textSpeed === 'instant' ? 0.3 : 1;
    if (ctx.settings.reduceMotion) this.timeScale = Math.min(this.timeScale, 0.6);
    registerDialogues(this.config.dialogues);

    this.cameras.main.setRoundPixels(true);
    this.buildStage();
    this.buildHud();
    this.buildDialogue(ctx);
    this.bindInput();
    this.installDebugHook();

    // Battle wants the director's one-shots (hit-spark / slash-arc), never its weather layers.
    this.effects = createEffects(this as unknown as IWorldScene, { layers: { rain: false, leaves: false, water: false, lantern: false, haze: false, lightning: false } });
    audio().playMusic(this.config.music.startsWith('music-') ? this.config.music : `music-${this.config.music}`, 600);
    this.setAmbience();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.onShutdown, this);
    bus.emitTyped(Events.BATTLE_START, { id: this.config.id });
    void this.uiFade(false, 300);
    void this.run();
  }

  // ------------------------------------------------------------------ build

  private buildStage(): void {
    const bgKey = ensureBattleBackground(this, this.config.background);
    this.add.image(0, 0, bgKey).setOrigin(0).setDepth(Depth.GROUND);
    this.engine.party().forEach((a, i) => this.addFighter(a, PARTY_ANCHORS[i] ?? PARTY_ANCHORS[0]!, 1, DEV_PALETTES[0]!));
    this.engine.enemies().forEach((a, i) => this.addFighter(a, ENEMY_ANCHORS[i] ?? ENEMY_ANCHORS[0]!, -1, DEV_PALETTES[1]!));
  }

  private addFighter(a: ActorState, home: { x: number; y: number }, dir: 1 | -1, devPal: BattleSpriteSpec['dev']): void {
    const spec: BattleSpriteSpec = this.config.sprites[a.id] ?? { ...DEFAULT_SPRITE, texture: a.sprite, facing: dir > 0 ? 'right' : 'left', dev: devPal };
    ensureBattleSprite(this, spec, DEV_LABEL[a.id] ?? 'DEV');
    const sprite = this.add.sprite(home.x, home.y, spec.texture).setOrigin(0.5, 1).setDepth(Depth.WORLD_BASE + home.y);
    // Shadow keeps the fighter grounded on the road.
    this.add.ellipse(home.x, home.y - 1, 34, 8, 0x1a120c, 0.32).setDepth(Depth.WORLD_BASE + home.y - 1);
    const f: Fighter = { state: a, sprite, spec, home, dir };
    if (a.side === 'enemy') {
      const top = home.y - spec.frameH - 8;
      f.nameTag = this.addText(home.x, top - 12, a.name, { fontSize: '12px', fontStyle: 'bold', color: HudCss.parchment, stroke: '#1a120c', strokeThickness: 3 }).setOrigin(0.5, 1);
      f.hpBar = new Bar(this, home.x - 36, top - 8, 72, 5, '', hpColor, { numbers: false });
      for (const o of f.hpBar.objects) (o as unknown as { setDepth(d: number): unknown }).setDepth(HUD_DEPTH);
      f.hpBar.set(a.hp, a.maxHp, false);
      f.intentTag = this.addText(home.x, top - 26, '', { fontSize: '11px', color: HudCss.lantern, stroke: '#1a120c', strokeThickness: 3 }).setOrigin(0.5, 1).setVisible(false);
    }
    this.fighters.set(a.id, f);
    this.playAnim(a.id, 'idle');
  }

  private buildHud(): void {
    const goal = this.config.end.goal === 'useSkill' ? `目标：使出「${this.skillName()}」并站到最后` : this.config.end.goal === 'landHits' ? '目标：命中老师' : '目标：站到最后';
    this.queue = new TurnQueue(this, 6, 6, GAME_W - 12, goal);
    this.queue.setRound(0, this.config.end.maxRounds);
    this.banner = new Banner(this, GAME_W / 2, 104);

    // Bottom band: status | log | commands. The shared dialogue box covers this band while a line plays.
    const y = 236, h = GAME_H - y - 6;
    const status = this.add.container(0, 0).setDepth(HUD_DEPTH);
    status.add(this.drawStatusPanel(6, y, 206, h));
    this.log = new BattleLog(this, 218, y, 194, h);
    this.menu = new CommandMenu(this, { x: 418, y, w: 216, h });
  }

  private drawStatusPanel(x: number, y: number, w: number, h: number): Phaser.GameObjects.GameObject[] {
    const student = this.engine.party()[0];
    const objs: Phaser.GameObjects.GameObject[] = [];
    const [fill, inner] = [this.add.rectangle(x, y, w, h, 0xf1e3c2, 0.94).setOrigin(0).setStrokeStyle(2, 0x8c2a22), this.add.rectangle(x + 3, y + 3, w - 6, h - 6).setOrigin(0).setStrokeStyle(1, 0xc9a54a, 0.55)];
    objs.push(fill, inner);
    objs.push(this.addText(x + 10, y + 8, student?.name ?? '', { fontSize: '15px', fontStyle: 'bold', color: HudCss.lacquer }));
    this.studentHp = new Bar(this, x + 10, y + 36, 110, 8, '体力', hpColor);
    this.studentSp = new Bar(this, x + 10, y + 58, 110, 8, '技力', 0x2b3a5c);
    this.studentTags = this.addText(x + 10, y + 80, '', { fontSize: '11px', color: HudCss.teal });
    objs.push(...this.studentHp.objects, ...this.studentSp.objects, this.studentTags);
    if (student) { this.studentHp.set(student.hp, student.maxHp, false); this.studentSp.set(student.sp, student.maxSp, false); }
    return objs;
  }

  private buildDialogue(ctx: ReturnType<typeof ctxOf>): void {
    this.box = resolveDialogueBox(this);
    const local = new Map(this.config.dialogues.map((d) => [d.id, d] as const));
    this.runner = createDialogueRunner(this.box, hooksFromContext(ctx), {
      host: { sfx: (k) => audio().sfx(k) },
      lookup: (id) => local.get(id),
    });
  }

  private bindInput(): void {
    installInputMap(this);
    const onDir = (p: { x: number; y: number }) => { if (this.menu.isOpen() && p.y !== 0) this.menu.move(p.y > 0 ? 1 : -1); };
    const onConfirm = () => {
      if (this.runner.isRunning()) { this.runner.advance(); return; }
      if (this.menu.isOpen()) this.menu.confirm();
    };
    bus.onTyped(Events.INPUT_DIR, onDir);
    bus.onTyped(Events.INPUT_CONFIRM, onConfirm);
    this.offs.push(() => bus.offTyped(Events.INPUT_DIR, onDir), () => bus.offTyped(Events.INPUT_CONFIRM, onConfirm));
  }

  private installDebugHook(): void {
    const hook: BattleDebugHook = {
      snapshot: () => this.engine.snapshot(),
      choose: (id) => this.menu.choose(id),
      isMenuOpen: () => this.menu.isOpen(),
      isTalking: () => this.runner.isRunning(),
      autoplay: (on) => this.setAutoplay(on),
      isAutoplay: () => this.auto,
    };
    window.__nantianBattle = hook;
    this.offs.push(() => { if (window.__nantianBattle === hook) delete window.__nantianBattle; });
  }

  private setAmbience(): void {
    const layers = (this.config.ambience ?? []).map((id) => (id.startsWith('amb-') ? id : `amb-${id}`));
    const director = audio() as { getManifest?: () => { entries: Record<string, unknown> } | null };
    const manifest = director.getManifest?.();
    const known = manifest ? layers.filter((k) => k in manifest.entries) : [];
    if (known.length) audio().setAmbience(known.map((key) => ({ key, volume: 0.6 })), 800);
  }

  // ------------------------------------------------------------------ flow

  private async run(): Promise<void> {
    await this.wait(350);
    await this.present(this.engine.start());
    let guard = 0;
    while (this.alive && !this.engine.isEnded() && guard++ < 200) {
      const cur = this.engine.current();
      if (!cur) break;
      if (cur.side === 'enemy') {
        const c = this.engine.chooseAi(cur.id);
        // The move is named only when 观察 revealed it this round; otherwise a generic 出手 (the log names it after the fact).
        const revealed = this.engine.intents.get(cur.id);
        const name = revealed && revealed.round === this.engine.round ? (this.engine.action(c.action)?.name ?? c.action) : '出手';
        this.banner.show(`${cur.name} · ${name}`);
        await this.wait(700);
        this.banner.hide();
        if (!this.alive) return;
        await this.present(this.engine.act(cur.id, c.action, c.target));
      } else {
        const views = this.engine.availableActions(cur.id);
        let choice: string;
        if (this.auto) {
          choice = this.autoChoice(views);
          await this.wait(200);
        } else {
          const picked = await this.untilAlive(this.menu.open(views));
          if (picked === null) return;
          choice = picked;
        }
        if (!this.alive) return;
        await this.present(this.engine.act(cur.id, choice));
      }
    }
    if (!this.alive) return;
    await this.wait(500);
    this.finish(this.engine.outcome ?? 'rounds');
  }

  private async present(events: BattleEvent[]): Promise<void> {
    for (const e of events) {
      if (!this.alive) return;
      const line = this.engine.describe(e);
      if (line) this.log.push(line);
      await this.presentOne(e);
    }
  }

  private async presentOne(e: BattleEvent): Promise<void> {
    switch (e.kind) {
      case 'start': return;
      case 'round': {
        this.queue.setRound(e.round, this.config.end.maxRounds);
        this.refreshQueue();
        this.banner.show(`第 ${e.round} 回合`, e.round === 1 ? '先看清老师的架势' : '');
        await this.wait(650);
        this.banner.hide();
        return;
      }
      case 'turn': {
        this.refreshQueue();
        const f = this.fighters.get(e.actor);
        if (f && !f.state.guarding) this.playAnim(e.actor, 'idle');
        this.refreshStatus();
        await this.wait(120);
        return;
      }
      case 'action': {
        const action = this.engine.action(e.action);
        const f = this.fighters.get(e.actor);
        if (!action || !f) return;
        if (action.kind === 'observe') { this.playAnim(e.actor, action.anim, true, 'idle'); floatText(this, f.home.x, f.home.y - f.spec.frameH - 4, '观察…', { color: HudCss.sand, size: 13 }); await this.wait(200); return; }
        if (action.kind === 'guard') { this.sfx(action.sfx ?? 'sword-guard'); this.playAnim(e.actor, action.anim, action.effect === 'rest', 'guard'); return; }
        // attack / skill: lunge towards the target while the swing anim plays.
        this.sfx(action.sfx ?? 'sword-swing');
        this.playAnim(e.actor, action.anim, true, 'attack');
        const reach = action.kind === 'skill' ? 40 : 24;
        await this.lunge(f, reach, 150);
        return;
      }
      case 'hit': {
        const t = this.fighters.get(e.target);
        const a = this.fighters.get(e.actor);
        const action = this.engine.action(e.action);
        if (t) {
          const cx = t.home.x + t.dir * 6, cy = t.home.y - t.spec.frameH * 0.55;
          const dir = (a?.dir ?? 1) > 0 ? 'right' : 'left';
          if (action?.fx === 'hit-spark' || action?.kind === 'skill') void playFx(this, 'hit-spark', cx, cy, { effects: this.effects, depth: Depth.WORLD_BASE + 400, params: { count: 12 } });
          else {
            void playFx(this, 'slash-arc', cx, cy, { flipX: dir === 'left', effects: this.effects, depth: Depth.WORLD_BASE + 400, params: { dir, radius: 20 } });
            void playFx(this, 'hit-spark', cx, cy - 6, { effects: this.effects, depth: Depth.WORLD_BASE + 401, scale: 0.75, params: { count: 7 } });
          }
          this.sfx(action?.kind === 'skill' ? 'hit-spark' : 'sword-hit');
          this.playAnim(e.target, 'hit', true);
          this.flash(t);
          void this.knockback(t, 8, 160);
          floatText(this, t.home.x, cy - 10, `${e.damage}`, { color: e.guarded ? '#9fe3dc' : '#fff1c2', size: e.guarded ? 15 : 18 });
          if (e.guarded) floatText(this, t.home.x + 18, cy - 4, '减半', { color: HudCss.teal, size: 11, rise: 14 });
          if (t.state.side === 'party') this.cameras.main.shake(140, 0.004);
          if (t.hpBar) t.hpBar.set(e.hp, t.state.maxHp);
        }
        this.refreshStatus();
        await this.wait(460);
        if (a) await this.returnHome(a, 120);
        return;
      }
      case 'miss': {
        const t = this.fighters.get(e.target);
        const a = this.fighters.get(e.actor);
        if (t) { floatText(this, t.home.x, t.home.y - t.spec.frameH - 6, '落空', { color: '#c9c9d1', size: 14 }); void this.dodge(t, 10, 180); }
        await this.wait(380);
        if (a) await this.returnHome(a, 120);
        return;
      }
      case 'feint': {
        const t = this.fighters.get(e.target);
        const a = this.fighters.get(e.actor);
        if (t) { floatText(this, t.home.x, t.home.y - t.spec.frameH - 6, '虚招！', { color: HudCss.lantern, size: 14 }); void this.dodge(t, 6, 200); }
        await this.wait(400);
        if (a) await this.returnHome(a, 120);
        return;
      }
      case 'guard': {
        const f = this.fighters.get(e.actor);
        if (f) {
          if (f.sprite.anims.currentAnim?.key !== battleAnimKey(f.spec.texture, 'guard')) this.playAnim(e.actor, 'guard');
          void playFx(this, 'guard-shimmer', f.home.x, f.home.y - f.spec.frameH / 2, { effects: this.effects, depth: Depth.WORLD_BASE + 399 });
          floatText(this, f.home.x, f.home.y - f.spec.frameH - 6, e.spDelta > 0 ? `防御 · 技力 +${e.spDelta}` : '防御', { color: HudCss.teal, size: 13 });
        }
        this.refreshStatus();
        await this.wait(420);
        return;
      }
      case 'rest': {
        const t = this.fighters.get(e.target);
        if (t && e.spDelta > 0) floatText(this, t.home.x, t.home.y - t.spec.frameH - 6, `技力 +${e.spDelta}`, { color: '#6fd3c8', size: 13 });
        this.refreshStatus();
        await this.wait(450);
        return;
      }
      case 'sp': {
        const f = this.fighters.get(e.actor);
        if (f && e.delta < 0) floatText(this, f.home.x - 14, f.home.y - f.spec.frameH - 2, `技力 ${e.delta}`, { color: '#9bb0d9', size: 11, rise: 14 });
        this.refreshStatus();
        return;
      }
      case 'observe': {
        const t = this.fighters.get(e.target);
        if (t?.intentTag && e.intent) t.intentTag.setText(`下一手：${e.intent.label}`).setVisible(true);
        this.banner.show(e.intent ? `看穿了：${e.intent.label}` : '看不出什么', e.comment);
        await this.wait(1100);
        this.banner.hide();
        this.refreshStatus();
        return;
      }
      case 'script': {
        this.menu.close();
        this.banner.hide();
        await this.say(e.dialogue);
        await this.wait(100);
        return;
      }
      case 'lowHp': {
        const f = this.fighters.get(e.actor);
        if (f) { floatText(this, f.home.x, f.home.y - f.spec.frameH - 8, '站立不稳', { color: HudCss.lantern, size: 14 }); this.cameras.main.flash(180, 140, 40, 30); }
        await this.wait(450);
        return;
      }
      case 'invalid': {
        const f = this.fighters.get(e.actor);
        if (f) floatText(this, f.home.x, f.home.y - f.spec.frameH - 6, e.reason, { color: HudCss.lantern, size: 12 });
        await this.wait(200);
        return;
      }
      case 'end': {
        // 招式学成: the tutor acknowledges (praise strip when the art has one), everyone else stands down.
        for (const f of this.fighters.values()) { this.playAnim(f.state.id, e.outcome === 'goal' && f.state.side === 'enemy' ? 'praise' : 'idle', false, 'idle'); f.intentTag?.setVisible(false); }
        this.refreshQueue(true);
        this.banner.show(e.outcome === 'goal' ? '切磋结束 · 招式学成' : e.outcome === 'rounds' ? '切磋结束 · 日头偏西' : '切磋中止 · 师父收剑', e.outcome === 'goal' ? `「${this.skillName()}」已记在心里` : '路还长，明日再练');
        await this.wait(1100);
        return;
      }
    }
  }

  private async say(dialogueId: string): Promise<void> {
    if (!this.alive) return;
    await new Promise<void>((resolve) => {
      const done = () => { this.waiters.delete(done); resolve(); };
      this.waiters.add(done);
      this.runner.run(dialogueId).then(done, done);
    });
  }

  /** Skill when affordable, else the first usable non-observe command (autoplay / harness). */
  private autoChoice(views: ReturnType<BattleEngine['availableActions']>): string {
    const usable = views.filter((v) => v.canUse && v.action.kind !== 'observe');
    return (usable.find((v) => v.action.kind === 'skill') ?? usable[0])?.action.id ?? 'attack';
  }

  /** Autoplay: a scene-level ticker advances lines; an open menu is answered at once. */
  private setAutoplay(on: boolean): void {
    this.auto = on;
    if (on && !this.autoTicker && this.alive) {
      this.autoTicker = this.time.addEvent({ delay: Math.max(60, Math.round(160 * this.timeScale)), loop: true, callback: () => { if (this.runner.isRunning()) this.runner.advance(); } });
    } else if (!on && this.autoTicker) { this.autoTicker.remove(); this.autoTicker = null; }
    if (on && this.menu.isOpen()) { const cur = this.engine.current(); if (cur) this.menu.choose(this.autoChoice(this.engine.availableActions(cur.id))); }
  }

  private finish(outcome: BattleOutcome): void {
    if (this.done) return;
    this.done = true;
    audio().playMusic(null, 800);
    bus.emitTyped(Events.BATTLE_END, { id: this.launch.configId, outcome });
    void this.uiFade(true, 320).then(() => {
      if (!this.alive) return;
      this.alive = false;
      const cb = this.launch.onDone;
      this.scene.stop();
      cb(outcome);
    });
  }

  // ------------------------------------------------------------------ presentation helpers

  private refreshQueue(allDone = false): void {
    const cur = this.engine.current()?.id ?? null;
    let seen = false;
    this.queue.set(this.engine.order.map((id) => {
      const a = this.engine.actor(id);
      let state: 'done' | 'current' | 'next' = 'next';
      if (allDone || cur === null) state = 'done';
      else if (id === cur) { state = 'current'; seen = true; }
      else state = seen ? 'next' : 'done';
      return { id, name: a.name, side: a.side, state };
    }));
    if (cur) {
      const f = this.fighters.get(cur);
      if (f?.state.side === 'enemy') f.intentTag?.setVisible(false);
    }
  }

  private refreshStatus(): void {
    const student = this.engine.party()[0];
    if (!student) return;
    this.studentHp.set(student.hp, student.maxHp);
    this.studentSp.set(student.sp, student.maxSp);
    const tags: string[] = [];
    if (student.guarding) tags.push('防御中：伤害减半');
    if (student.observed) tags.push('本回合已观察');
    const goal = this.engine.goalMet();
    if (this.config.end.goal === 'useSkill') tags.push(goal ? `「${this.skillName()}」已使出` : `「${this.skillName()}」尚未使出`);
    this.studentTags.setText(tags);
    for (const f of this.fighters.values()) if (f.hpBar) f.hpBar.set(f.state.hp, f.state.maxHp);
  }

  private skillName(): string { return this.config.actions.find((a) => a.kind === 'skill')?.name ?? '剑招'; }

  /**
   * Plays `<texture>:<anim>`; an anim the sheet lacks (an extra strip such as observe/praise, or a data
   * typo) falls back to `fallback` (a canonical BattleAnimName) so a fighter is never left mid-pose.
   * `chainRest` returns to idle (or guard while guarding) once a one-shot anim completes.
   */
  private playAnim(id: string, anim: string, chainRest = false, fallback: BattleAnimName = 'idle'): void {
    const f = this.fighters.get(id);
    if (!f) return;
    let key = battleAnimKey(f.spec.texture, anim);
    if (!this.anims.exists(key)) {
      key = battleAnimKey(f.spec.texture, fallback);
      if (!this.anims.exists(key)) return;
    }
    if (f.sprite.anims.currentAnim?.key === key && f.spec.anims[anim]?.loop) return;
    f.sprite.play(key);
    if (chainRest) {
      const rest = battleAnimKey(f.spec.texture, f.state.guarding ? 'guard' : 'idle');
      if (this.anims.exists(rest)) f.sprite.chain(rest);
    }
  }

  private lunge(f: Fighter, px: number, ms: number): Promise<void> {
    return this.tweenX(f, f.home.x + f.dir * px, ms);
  }
  private returnHome(f: Fighter, ms: number): Promise<void> { return this.tweenX(f, f.home.x, ms); }
  private knockback(f: Fighter, px: number, ms: number): Promise<void> {
    return this.tweenX(f, f.home.x - f.dir * px, ms).then(() => this.tweenX(f, f.home.x, ms));
  }
  private dodge(f: Fighter, px: number, ms: number): Promise<void> { return this.knockback(f, px, ms); }

  private tweenX(f: Fighter, x: number, ms: number): Promise<void> {
    if (!this.alive) return Promise.resolve();
    const d = Math.max(1, Math.round(ms * this.timeScale));
    return new Promise<void>((resolve) => {
      const done = () => { this.waiters.delete(done); resolve(); };
      this.waiters.add(done);
      const proxy = { x: f.sprite.x };
      this.tweens.add({ targets: proxy, x, duration: d, ease: 'Sine.easeOut', onUpdate: () => f.sprite.setX(Math.round(proxy.x)), onComplete: done });
    });
  }

  private flash(f: Fighter): void {
    f.sprite.setTint(0xfff1c2).setTintMode(Phaser.TintModes.FILL);
    this.time.delayedCall(Math.round(70 * this.timeScale) + 1, () => { if (f.sprite.active) f.sprite.clearTint().setTintMode(Phaser.TintModes.MULTIPLY); });
  }

  private sfx(id: string): void { audio().sfx(id.startsWith('sfx-') || id.startsWith('ui-') ? id : `sfx-${id}`); }

  private addText(x: number, y: number, s: string, style: Phaser.Types.GameObjects.Text.TextStyle): Phaser.GameObjects.Text {
    return battleText(this, x, y, s, style).setDepth(HUD_DEPTH);
  }

  /** Resolves with the value, or null once the scene shuts down (so no loop outlives the scene). */
  private untilAlive<T>(p: Promise<T>): Promise<T | null> {
    if (!this.alive) return Promise.resolve(null);
    return new Promise<T | null>((resolve) => {
      const done = () => { this.waiters.delete(done); resolve(null); };
      this.waiters.add(done);
      p.then((v) => { this.waiters.delete(done); resolve(v); }, () => done());
    });
  }

  private uiFade(out: boolean, ms: number): Promise<void> {
    const ui = this.scene.get(SceneKeys.UI) as (Phaser.Scene & { fade?: (out: boolean, ms: number) => Promise<void> }) | null;
    if (ui?.fade && ui.scene.isActive()) return ui.fade(out, Math.round(ms * this.timeScale)).catch(() => undefined);
    return Promise.resolve();
  }

  /** Shutdown-safe delay scaled by the presentation speed. */
  private wait(ms: number): Promise<void> {
    const d = Math.round(ms * this.timeScale);
    if (!this.alive || d <= 0) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const done = () => { this.waiters.delete(done); resolve(); };
      this.waiters.add(done);
      this.time.delayedCall(d, done);
    });
  }

  private onShutdown(): void {
    this.alive = false;
    if (this.autoTicker) { this.autoTicker.remove(); this.autoTicker = null; }
    for (const off of this.offs.splice(0)) off();
    try { this.runner?.abort(); } catch { /* ignore */ }
    this.menu?.close();
    for (const w of [...this.waiters]) w();
    this.waiters.clear();
    this.fighters.clear();
    this.effects = null;
    if (!this.done) { this.done = true; this.launch.onDone(this.engine?.outcome ?? 'rounds'); }
  }
}
